import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { ArrowUp } from 'lucide-react';
import CollectionSidebar from '@/features/collections/components/Sidebar';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { useOptimizedCollectionNotes } from '../hooks/useOptimizedCollectionNotes';
import { useCollectionWithFirstPage } from '../hooks/useCollectionWithFirstPage';

type CollectionLayoutProps = {
  children: ReactNode;
  Menu?: ReactNode;
};

const SharedCollectionLayout = ({ children, Menu }: CollectionLayoutProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const setCurrentCollections = useCollectionStore(state => state.setCurrentCollection);
  const setCollectionStructure = useCollectionStore(state => state.setCollectionStructure);

  const { collectionId, pageId } = useParams<{ collectionId: string; pageId: string }>();

  const { data: collectionData } = useCollectionWithFirstPage(collectionId);
  const { structure } = useOptimizedCollectionNotes();

  useEffect(() => {
    if (!collectionData) return;
    const { collection, firstPage } = collectionData;
    setCurrentCollections(collection);
    setCollectionStructure(structure || []);

    // If we're already on a specific page, don't redirect
    if (pageId) return;

    // If we don't have a first page, we can't redirect
    if (!firstPage?.id) return;

    // Always redirect to first page when on collection root
    navigate(`/shared/${collectionId as string}/page/${firstPage.id}`, { replace: true });
  }, [
    collectionId,
    collectionData,
    pageId,
    structure,
    navigate,
    setCollectionStructure,
    setCurrentCollections,
  ]);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToTop();
        }, 100);
      });
    }
  }, [pageId, collectionId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollButton(container.scrollTop > 400);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="flex h-screen">
      <CollectionSidebar
        onBackClick={() => {
          navigate('/shared');
        }}
      />
      <main ref={containerRef} className="flex-1 overflow-y-auto relative overflow-x-hidden">
        {Menu}
        {children}
        {showScrollButton && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-4 right-4 h-8 w-8 p-0 z-1000"
            variant="secondary"
            size="icon"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </main>
    </div>
  );
};

export default memo(SharedCollectionLayout);
